'use client';

import { Children, type HTMLAttributes, type ReactNode } from 'react';

import { useDevice } from '@/contexts/DeviceContext';
import { cn } from '@/libs';

import { useCarousel } from './hooks/useCarousel';

import { Icon } from '../Icon';

type CarouselProps = HTMLAttributes<HTMLUListElement> & {
  center?: boolean;
  children: ReactNode;
};

export const Carousel = ({ center = true, className, children }: CarouselProps) => {
  const { isMobile } = useDevice();

  const { chevLeft, chevRight, containerRef, scrollLeft, scrollRight, isReady } =
    useCarousel();

  return (
    <div className="relative w-full">
      {!isMobile && isReady && (
        <>
          <div
            className={cn(
              'absolute pointer-events-none z-10 w-28 h-full left-0 top-0 bg-gradient-to-l from-transparent to-background transition-opacity',
              chevLeft && 'opacity-0',
            )}
          />
          <button
            aria-label="Prev"
            onClick={scrollLeft}
            className={cn(
              'absolute flex items-center text-oc-4 top-1/2 -translate-y-1/2 bg-gray-600 hover:bg-gray-500 rounded-full p-2 z-20 transition-transform h-8 w-8 left-0',
              chevLeft && 'scale-0',
            )}
          >
            <Icon.ChevronLeft className="h-4 w-4" />
          </button>
        </>
      )}

      <ul
        ref={containerRef}
        className={cn(
          'flex items-stretch gap-2 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth scrollbar-hide',
          className,
        )}
      >
        {isMobile && (
          <div className="absolute pointer-events-none z-10 w-28 h-full right-0 top-0 bg-gradient-to-r from-transparent to-primary transition-opacity" />
        )}
        {Children.map(children, (item) => (
          <li
            className={cn(
              'relative flex-shrink-0',
              center ? 'snap-center' : 'snap-start',
            )}
          >
            {item}
          </li>
        ))}
      </ul>
      {!isMobile && isReady && (
        <>
          <div
            className={cn(
              'absolute pointer-events-none z-10 w-28 h-full right-0 top-0 bg-gradient-to-r from-transparent to-background transition-opacity',
              chevRight && 'opacity-0',
            )}
          />
          <button
            aria-label="Next"
            onClick={scrollRight}
            className={cn(
              'absolute flex items-center text-oc-4 top-1/2 -translate-y-1/2 bg-gray-600 hover:bg-gray-500 rounded-full p-2 z-20 transition-transform h-8 w-8 right-0',
              chevRight && 'scale-0',
            )}
          >
            <Icon.ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
};
